import { useState } from 'react';
import { Plus, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SubcontractorInlineForm } from '../components/SubcontractorInlineForm';
import { useSubcontractors } from '../hooks/useSubcontractors';
import { useSubcontractor } from '../hooks/useSubcontractor';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { deleteSubcontractor } from '../api/subcontractors';
import { getApiErrorMessage } from '@/lib/api-error';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { SubcontractorDto } from '../types';

export function SubcontractorsPage() {
  usePageTitle('Subcontractors');
  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const { hasPermission } = useAuth();
  const canCreate = hasPermission(Permissions.SubcontractorsCreate);
  const canUpdate = hasPermission(Permissions.SubcontractorsUpdate);

  const { data: subcontractors, isLoading: isLoadingList, error: listError, refetch: refetchList } =
    useSubcontractors(showInactive ? undefined : { isActive: true });

  const { data: selectedSubcontractor, isLoading: isLoadingDetail, refetch: refetchDetail } =
    useSubcontractor(selectedSubcontractorId ?? undefined);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedSubcontractorId(value ? parseInt(value) : null);
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setSelectedSubcontractorId(null);
    setIsAddingNew(true);
  };

  const handleSave = (saved: SubcontractorDto) => {
    refetchList();
    setSelectedSubcontractorId(saved.id);
    setIsAddingNew(false);
    setPageError(null);
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
  };

  const handleDeactivate = async () => {
    if (!selectedSubcontractorId) return;
    try {
      await deleteSubcontractor(selectedSubcontractorId);
      setSelectedSubcontractorId(null);
      setPageError(null);
      refetchList();
    } catch (err) {
      setPageError(getApiErrorMessage(err, 'Failed to deactivate subcontractor'));
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-6 space-y-4">
      {/* Header */}
      <div className="bg-card rounded-[10px] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h2 className="text-lg font-semibold text-secondary">Subcontractors</h2>

          <div className="flex items-center gap-4">
            {/* Record Selector */}
            <div className="w-[300px]">
              <Select
                value={selectedSubcontractorId?.toString() ?? ''}
                onChange={handleSelect}
                disabled={isLoadingList}
              >
                <option value="">Select a subcontractor...</option>
                {[...subcontractors].sort((a, b) => a.companyName.localeCompare(b.companyName)).map((s) => (
                  <option key={s.id} value={s.id.toString()}>
                    {s.companyName}{!s.isActive ? ' (Inactive)' : ''}
                  </option>
                ))}
              </Select>
            </div>

            {/* Show Inactive Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showInactive"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Label htmlFor="showInactive" className="text-sm font-normal cursor-pointer">
                Show inactive
              </Label>
            </div>

            {/* Add Button */}
            {canCreate && (
              <Button onClick={handleAddNew} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Subcontractor
              </Button>
            )}

            {/* Actions Menu */}
            {selectedSubcontractorId && canUpdate && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem
                    onClick={() => setIsConfirmOpen(true)}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    Deactivate
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert error={listError} className="rounded-[20px] p-4" />
      <ErrorAlert error={pageError} className="rounded-[20px] p-4" />

      {/* Detail Content */}
      {isLoadingDetail && selectedSubcontractorId && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" label="Loading subcontractor..." />
        </div>
      )}

      {isAddingNew && (
        <SubcontractorInlineForm
          key="new"
          subcontractor={null}
          onSave={handleSave}
          onCancel={handleCancelAdd}
        />
      )}

      {selectedSubcontractor && !isAddingNew && (
        <SubcontractorInlineForm
          key={selectedSubcontractor.id}
          subcontractor={selectedSubcontractor}
          onSave={(saved) => {
            refetchList();
            refetchDetail();
            setSelectedSubcontractorId(saved.id);
          }}
        />
      )}

      {!selectedSubcontractorId && !isAddingNew && !isLoadingList && (
        <div className="text-center py-12 text-gray-500">
          Select a subcontractor to view details
        </div>
      )}

      {/* Deactivate Confirmation */}
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Deactivate Subcontractor"
        message={`Are you sure you want to deactivate "${selectedSubcontractor?.companyName}"? This will make the subcontractor inactive.`}
        confirmLabel="Deactivate"
        variant="destructive"
        onConfirm={handleDeactivate}
      />
    </div>
  );
}
